import { useEffect, useRef, useState, useCallback } from 'react';

// Hook for handling data loading with caching
export function useDataLoader(
  loadFn: () => Promise<void>,
  options = { reloadOnFocus: true }
) {
  const loadingRef = useRef(false);
  const timeoutRef = useRef<number>();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (loadingRef.current || !mountedRef.current) return;
      
      try {
        loadingRef.current = true;
        await loadFn();
      } catch (error) {
        console.error('Data loading error:', error);
      } finally {
        if (mountedRef.current) {
          loadingRef.current = false;
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && options.reloadOnFocus) {
        // Clear any pending timeout
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }

        // Debounce the reload
        timeoutRef.current = window.setTimeout(() => {
          if (mountedRef.current) {
            load();
          }
        }, 1000);
      }
    };

    // Load data initially
    load();

    // Set up visibility change listener if reloadOnFocus is enabled
    if (options.reloadOnFocus) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [loadFn, options.reloadOnFocus]);
}

// Synchronous data loader that blocks rendering until data is ready
// This prevents race conditions and empty state caching issues
export function useDataLoaderSync(
  loadFn: () => Promise<void>,
  dependencies: any[] = [],
  options = { timeout: 10000 }
) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    
    const load = async () => {
      if (!mounted || !mountedRef.current) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Set up timeout to prevent infinite loading
        if (options.timeout > 0) {
          timeoutRef.current = setTimeout(() => {
            if (mounted && mountedRef.current) {
              setError(new Error('Data loading timeout'));
              setLoading(false);
            }
          }, options.timeout);
        }
        
        await loadFn();
        
        // Clear timeout on success
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
      } catch (err) {
        if (mounted && mountedRef.current) {
          setError(err as Error);
        }
      } finally {
        if (mounted && mountedRef.current) {
          setLoading(false);
        }
      }
    };
    
    load();
    
    return () => { 
      mounted = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, dependencies);
  
  return { loading, error };
}

// Export other hooks...
export * from './hooks';